import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { roleAllowed } from "@/lib/roles";
import { buildCustomerMap } from "@/lib/customerNames";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ShoppingBag, Mail, Printer, Download, XCircle, FileEdit, Truck, ExternalLink,
  ClipboardCheck, AlertCircle, Receipt, Save, PackageSearch, PackageOpen, FileCheck2,
} from "lucide-react";
import { toast } from "sonner";
import { canBlockStock, StockBlockButton, useOrderStock, useOneOrderStock, OrderStockBadge, OrderStockPanel } from "@/components/OrderStock";
import RevisionHistoryButton from "@/components/RevisionHistory";
import { PartialDeliveryList } from "@/components/PartialDeliveryEditor";
import { revisionNo } from "@/lib/revisionNo";
import { formatMoney, docCurrency, currencySymbol } from "@/lib/currency";
import OrderEmailStatus from "@/components/OrderEmailStatus";

const STATUS = {
  draft: "bg-slate-100 text-slate-600",
  confirmed: "bg-blue-50 text-blue-700",
  dispatched: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

const UOMS = ["PCS", "KG", "MTR", "SET", "BOX", "NOS", "LTR"];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function CustomerSalesOrdersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerNames, setCustomerNames] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedTotals, setSelectedTotals] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ lines: [], delivery_date: "", notes: "", reason: "" });
  // "edit" fixes a typo in place; "revise" issues the next revision (Rev N),
  // keeps the prior version and auto-emails the customer + marketing person.
  const [editMode, setEditMode] = useState("edit");

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailPayload, setEmailPayload] = useState({ to_email: "", cc_email: "", bcc_email: "", subject: "", message: "" });

  const [raiseDcOpen, setRaiseDcOpen] = useState(false);
  const [raiseDcForm, setRaiseDcForm] = useState({ vehicle_no: "", driver_name: "", transporter: "", notes: "" });
  // Direct DC (no packed Packing Slip): qty per item, defaulting to what is
  // still to be delivered. null = raising from the packed slip.
  const [raiseDcLines, setRaiseDcLines] = useState(null);

  const custMap = useMemo(() => buildCustomerMap(customers, customerNames), [customers, customerNames]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  // Edit/Cancel — the backend applies no role check at all (any user who can
  // reach this Sales Order can fix a rate/date typo or cancel it), so these
  // are just "is a logged-in user on this page" rather than a role allow-list.
  const canEdit = !!user;
  const canCancel = !!user;
  // Matches POST /customer-sales-orders/{id}/raise-dc's role check exactly
  // (admin/planner/operator, plus whatever those alias to — see roleAllowed).
  const canRaiseDc = roleAllowed(user?.role, ["admin", "planner", "operator"]);
  // Matches POST /packing-slips's role check — a wider set than Raise DC
  // (also sales_crm, marketing, stores) since a Packing Slip is the routine
  // pre-dispatch prep step, not the dispatch action itself.
  // Tax Invoices are raised per dispatched DC (same rule and roles as the
  // Delivery Challans page's "Generate Tax Invoice").
  const canGenerateInvoice = roleAllowed(user?.role, ["admin", "planner", "operator"]);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceDcs, setInvoiceDcs] = useState(null);
  const canCreatePackingSlip = roleAllowed(user?.role, ["admin", "planner", "operator", "sales_crm", "marketing", "stores"]);
  // Matches POST /packing-slips/{id}/unpack and /regenerate.
  const canRepack = roleAllowed(user?.role, ["admin", "planner", "operator", "production_manager", "stores"]);

  const load = async () => {
    const [s, c, n, i] = await Promise.all([
      api.get("/customer-sales-orders"),
      api.get("/customers"),
      api.get("/customers/names-lookup"),
      api.get("/items"),
    ]);
    setList(s.data);
    setCustomers(c.data);
    setCustomerNames(n.data);
    setItems(i.data);
    if (selected) setSelected(s.data.find((x) => x.id === selected.id) || null);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Deep-link support — e.g. "View Sales Order" from the Proforma Invoice page.
  useEffect(() => {
    const id = location.state?.selectId;
    if (id && list.length) {
      const found = list.find((x) => x.id === id);
      if (found) setSelected(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectId, list.length]);

  useEffect(() => {
    if (!selected) { setSelectedTotals(null); return; }
    api.get(`/customer-sales-orders/${selected.id}/totals`).then((r) => setSelectedTotals(r.data)).catch(() => setSelectedTotals(null));
  }, [selected]);

  // Stock reserved for each order — keyed by its source Transfer Order,
  // which is what holds the reservation (see /stock/order-reservations).
  const [stockTick, setStockTick] = useState(0);
  const selectedStock = useOneOrderStock(selected ? selected.source_to_id : null, stockTick);
  const orderStock = useOrderStock(list.map((so) => so.source_to_id), list, stockTick);

  const visibleList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((so) => {
      if (statusFilter !== "all" && so.status !== statusFilter) return false;
      if (!q) return true;
      const cust = custMap[so.customer_id];
      return (
        (so.code || "").toLowerCase().includes(q) ||
        (so.source_to_code || "").toLowerCase().includes(q) ||
        (so.dc_codes?.length ? so.dc_codes.join(" ") : so.dc_code || "").toLowerCase().includes(q) ||
        (so.po_reference || "").toLowerCase().includes(q) ||
        (cust?.name || so.customer_name || "").toLowerCase().includes(q) ||
        (cust?.code || "").toLowerCase().includes(q)
      );
    });
  }, [list, search, statusFilter, custMap]);

  const openEdit = (so, mode = "edit") => {
    setEditMode(mode);
    setEditForm({
      lines: (so.lines || []).map((l) => ({
        item_id: l.item_id, price: l.price, discount_pct: l.discount_pct || 0,
        cgst_pct: l.cgst_pct || 0, sgst_pct: l.sgst_pct || 0, igst_pct: l.igst_pct || 0,
        uom: l.uom || "",
      })),
      delivery_date: so.delivery_date ? new Date(so.delivery_date).toISOString().slice(0, 10) : "",
      notes: so.notes || "",
      reason: "",
    });
    setEditOpen(true);
  };

  const setEditLine = (idx, patch) => setEditForm((f) => ({
    ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));

  const saveEdit = async () => {
    setBusy(true);
    try {
      const body = {
        lines: editForm.lines,
        delivery_date: editForm.delivery_date ? new Date(editForm.delivery_date).toISOString() : null,
        notes: editForm.notes,
      };
      if (editMode === "revise") {
        const { data } = await api.post(`/customer-sales-orders/${selected.id}/revise`, { ...body, reason: editForm.reason });
        const em = data.auto_email || {};
        toast.success(`${selected.code} revised — ${data.revision_no || revisionNo(selected.code, data.revision_number)}` +
          (em.sent ? " — emailed to customer & marketing" : em.queued ? " — email queued" : ""));
      } else {
        await api.patch(`/customer-sales-orders/${selected.id}`, body);
        toast.success(`${selected.code} updated`);
      }
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) return toast.error("A cancellation reason is required");
    setBusy(true);
    try {
      await api.post(`/customer-sales-orders/${selected.id}/cancel`, { reason: cancelReason.trim() });
      toast.success(`${selected.code} cancelled`);
      setCancelOpen(false);
      setCancelReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  };

  const openEmail = (so) => {
    const target = so || selected;
    if (!target) return;
    const cust = custMap[target.customer_id] || {};
    // Auto-fill Cc with the Sales Order creator's email — created_by is set
    // from the logged-in user who approved production (the action that
    // auto-raises this Sales Order) and persisted on the document. Only
    // pre-fill when it looks like an email — legacy/system rows carry
    // "_system" there instead. Stays a plain editable field either way.
    const creatorEmail = target.created_by && target.created_by.includes("@") ? target.created_by : "";
    setEmailPayload({
      to_email: target.customer_email || cust.email || "",
      cc_email: creatorEmail,
      bcc_email: "",
      subject: `Sales Order ${target.code}`,
      message: "Please find the attached Sales Order. Reply to this email for any clarifications.",
    });
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!emailPayload.to_email) return toast.error("Recipient email is required");
    setBusy(true);
    try {
      await api.post(`/customer-sales-orders/${selected.id}/email`, {
        to_email: emailPayload.to_email,
        cc_email: emailPayload.cc_email || null,
        bcc_email: emailPayload.bcc_email || null,
        subject: emailPayload.subject || null,
        message: emailPayload.message || "",
      });
      toast.success(`Sent to ${emailPayload.to_email}`);
      setEmailOpen(false);
      await load(); // picks up email_sent so the SENT badge shows immediately
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed");
    } finally { setBusy(false); }
  };

  // One-click "Save" — emails the Sales Order PDF straight to the customer's
  // on-file address without opening the compose dialog. Falls back to the
  // dialog (pre-filled) only when there's no address to send to, so the
  // action never fails silently or throws an unhandled error.
  const quickSendEmail = async (so) => {
    const cust = custMap[so.customer_id] || {};
    const toEmail = (so.customer_email || cust.email || "").trim();
    if (!toEmail) {
      toast.error("This customer has no email on file — enter one to send.");
      setSelected(so);
      openEmail(so);
      return;
    }
    setBusy(true);
    try {
      const creatorEmail = so.created_by && so.created_by.includes("@") ? so.created_by : null;
      await api.post(`/customer-sales-orders/${so.id}/email`, {
        to_email: toEmail,
        cc_email: creatorEmail,
        bcc_email: null,
        subject: `Sales Order ${so.code}`,
        message: "Please find the attached Sales Order. Reply to this email for any clarifications.",
      });
      toast.success(`${so.code} sent to ${toEmail}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/customer-sales-orders/${selected.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `SalesOrder-${selected.code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.code}.pdf`);
    } catch { toast.error("PDF download failed"); }
  };

  const printSO = async () => {
    const r = await api.get(`/customer-sales-orders/${selected.id}/render-html`, { responseType: "text" });
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${selected.code}</title></head><body>${r.data}<script>window.onload=()=>setTimeout(()=>window.print(),200)</script></body></html>`);
    w.document.close();
  };

  const openRaiseDc = (so) => {
    const packed = so.fulfilment?.open_packing_slip_status === "packed";
    setRaiseDcLines(packed ? null : (so.fulfilment?.lines || [])
      .filter((l) => l.pending_pack_qty > 0)
      .map((l) => ({ item_id: l.item_id, max: l.pending_pack_qty, qty: l.pending_pack_qty })));
    setRaiseDcOpen(true);
  };

  const submitRaiseDc = async () => {
    const lines = raiseDcLines && raiseDcLines.map((l) => ({ item_id: l.item_id, qty: Number(l.qty || 0) })).filter((l) => l.qty > 0);
    if (raiseDcLines && !lines.length) return toast.error("Enter a quantity for at least one item");
    setBusy(true);
    try {
      const { data } = await api.post(`/customer-sales-orders/${selected.id}/raise-dc`, { ...raiseDcForm, ...(lines ? { lines } : {}) });
      toast.success(`Delivery Challan ${data.delivery_challan.code} raised`);
      setRaiseDcOpen(false);
      setRaiseDcForm({ vehicle_no: "", driver_name: "", transporter: "", notes: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Raise DC failed");
    } finally { setBusy(false); }
  };

  const viewTransferOrder = (so) => navigate("/sales-orders", { state: { selectId: so.source_to_id } });
  const viewDc = (so, dcId) => navigate("/delivery-challans", { state: { selectId: dcId || so.dc_id } });
  const raisePi = (so) => navigate("/proforma-invoices", { state: { presetCsoId: so.id } });
  const raiseBalancePi = async (so) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/customer-sales-orders/${so.id}/balance-proforma-invoice`);
      toast.success(`Balance Proforma Invoice ${data.code} raised`);
      navigate("/proforma-invoices", { state: { selectId: data.id } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not raise Balance PI");
    } finally { setBusy(false); }
  };
  const viewPackingSlip = (so, psId) => navigate("/packing-slips", { state: { selectId: psId || so.packing_slip_id } });
  // Every DC raised against this Sales Order (it can be dispatched over
  // several), oldest first — falls back to the single dc_id/dc_code on
  // records older than the dc_ids/dc_codes lists.
  const soDcs = (so) => {
    const codes = so.dc_codes?.length ? so.dc_codes : (so.dc_code ? [so.dc_code] : []);
    const ids = so.dc_ids?.length ? so.dc_ids : (so.dc_id ? [so.dc_id] : []);
    return codes.map((code, i) => ({ code, id: ids[i] }));
  };
  const openTaxInvoices = async (so) => {
    setInvoiceOpen(true);
    setInvoiceDcs(null);
    const rows = await Promise.all(soDcs(so).filter((d) => d.id).map((d) =>
      api.get(`/delivery-challans/${d.id}`).then((r) => r.data).catch(() => ({ id: d.id, code: d.code, status: "unknown" }))));
    setInvoiceDcs(rows);
  };
  const viewInvoice = (dc) => navigate("/tax-invoices", { state: { selectId: dc.tax_invoice_id } });
  const generateInvoice = async (dc) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/delivery-challans/${dc.id}/generate-tax-invoice`, {});
      toast.success(`Tax Invoice ${data.code} generated`);
      navigate("/tax-invoices", { state: { selectId: data.id } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generate Tax Invoice failed");
    } finally { setBusy(false); }
  };
  // Packing happens right here on the Sales Order, from FG stock: the slip
  // packs each line up to what is free in FG (the API refuses when nothing
  // is), and can be unpacked / re-packed without leaving the order.
  const slipAction = async (fn, okMsg, errMsg) => {
    setBusy(true);
    try {
      const msg = okMsg(await fn());
      toast.success(msg);
      await load();
      setStockTick((t) => t + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || errMsg);
    } finally { setBusy(false); }
  };
  const packedSummary = (ps) => {
    const packed = (ps.lines || []).reduce((a, l) => a + Number(l.packed_qty || 0), 0);
    const short = (ps.lines || []).some((l) => Number(l.packed_qty || 0) < Number(l.ordered_qty || 0));
    return `${packed} packed from FG stock${short ? " — rest pending until FG stock is available" : ""}`;
  };
  const createPackingSlip = (so) => slipAction(
    () => api.post("/packing-slips", { customer_so_id: so.id }).then((r) => r.data),
    (ps) => `Packing Slip ${ps.code}: ${packedSummary(ps)}`, "Could not create Packing Slip");
  const packFromFg = (psId) => slipAction(
    () => api.post(`/packing-slips/${psId}/regenerate`).then((r) => r.data),
    (ps) => (ps.status === "packed" ? `Packing Slip ${ps.code}: ${packedSummary(ps)}` : `No FG stock available to pack ${ps.code} yet`),
    "Could not pack from FG stock");
  const unpackSlip = (psId) => slipAction(
    () => api.post(`/packing-slips/${psId}/unpack`).then((r) => r.data),
    (ps) => `Packing Slip ${ps.code} unpacked — its FG stock is free again`, "Unpack failed");

  const pendingCount = (so) => (so.lines || []).filter((l) => l.pending).length;

  return (
    <div className="space-y-6" data-testid="cso-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Sales Order
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Auto-created the moment a Transfer Order's delivery date is confirmed at Production Approval.
          </p>
        </div>
      </div>

      {/* Tax Invoice — one per dispatched DC of this order */}
      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Tax Invoice — {selected?.code}</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm" data-testid="cso-tax-invoice-dialog">
            {invoiceDcs === null ? (
              <div className="text-slate-500">Loading delivery challans…</div>
            ) : invoiceDcs.length === 0 ? (
              <div className="text-slate-500">No Delivery Challan has been raised for this order yet. A Tax Invoice is raised against a dispatched DC — raise and dispatch a DC first.</div>
            ) : invoiceDcs.map((dc) => (
              <div key={dc.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-2">
                <div>
                  <div className="mono font-semibold text-slate-900">{dc.code}</div>
                  <div className="text-xs text-slate-500 capitalize">{dc.status === "draft" ? "ready" : dc.status}{dc.tax_invoice_code ? ` · Invoice ${dc.tax_invoice_code}` : ""}</div>
                </div>
                {dc.tax_invoice_id ? (
                  <Button size="sm" variant="outline" onClick={() => viewInvoice(dc)} data-testid={`cso-view-invoice-${dc.code}`}>
                    <FileCheck2 className="w-4 h-4 mr-1.5" />View Invoice
                  </Button>
                ) : ["dispatched", "delivered"].includes(dc.status) ? (
                  canGenerateInvoice ? (
                    <Button size="sm" onClick={() => generateInvoice(dc)} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid={`cso-generate-invoice-${dc.code}`}>
                      <FileCheck2 className="w-4 h-4 mr-1.5" />Generate Tax Invoice
                    </Button>
                  ) : <span className="text-xs text-slate-400">Not invoiced yet</span>
                ) : (
                  <span className="text-xs text-slate-400">Dispatch the DC to invoice it</span>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {editMode === "revise" ? `Revise ${revisionNo(selected?.code, selected?.revision_number)} → ${revisionNo(selected?.code, (selected?.revision_number || 0) + 1)}` : `Edit ${selected?.code}`}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-5">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              Item and quantity are fixed from Production Approval — rate, discount, UOM, delivery date and notes can change here.
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-700 mb-2">Line Items</div>
              <div className="space-y-3">
                {editForm.lines.map((ln, idx) => {
                  const it = itemMap[ln.item_id];
                  return (
                    <div key={idx} className="border border-slate-200 rounded-md p-3 bg-white">
                      <div className="text-sm font-medium text-slate-800 mb-2">{it?.code} — {it?.name}</div>
                      <div className="grid grid-cols-5 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase">Rate ({currencySymbol(docCurrency(selected))})</Label>
                          <Input type="number" value={ln.price} onChange={(e) => setEditLine(idx, { price: parseFloat(e.target.value || 0) })} data-testid={`cso-edit-price-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase">Disc %</Label>
                          <Input type="number" value={ln.discount_pct} onChange={(e) => setEditLine(idx, { discount_pct: parseFloat(e.target.value || 0) })} data-testid={`cso-edit-disc-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase">CGST %</Label>
                          <Input type="number" value={ln.cgst_pct} onChange={(e) => setEditLine(idx, { cgst_pct: parseFloat(e.target.value || 0) })} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase">SGST %</Label>
                          <Input type="number" value={ln.sgst_pct} onChange={(e) => setEditLine(idx, { sgst_pct: parseFloat(e.target.value || 0) })} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase">UOM</Label>
                          <Select value={ln.uom || "PCS"} onValueChange={(v) => setEditLine(idx, { uom: v })}>
                            <SelectTrigger data-testid={`cso-edit-uom-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {UOMS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Delivery Date</Label>
                <Input type="date" value={editForm.delivery_date} onChange={(e) => setEditForm({ ...editForm, delivery_date: e.target.value })} data-testid="cso-edit-delivery-date" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="cso-edit-notes" />
            </div>
            {editMode === "revise" && (
              <div>
                <Label>Reason for Revision</Label>
                <Textarea rows={2} value={editForm.reason} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} placeholder="e.g. Rate revised as agreed with customer" data-testid="cso-revise-reason" />
                <p className="text-[11px] text-slate-500 mt-1">The revised Sales Order is emailed to the customer, Cc the marketing person who raised the Transfer Order.</p>
              </div>
            )}
          </div>
          <SheetFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="cso-edit-save">
              {editMode === "revise" ? "Issue Revision & Email" : "Save Changes"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cancel {selected?.code}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason *</Label>
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Why is this order being cancelled?" data-testid="cso-cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Back</Button>
            <Button variant="destructive" onClick={submitCancel} disabled={busy} data-testid="cso-cancel-confirm">
              <XCircle className="w-4 h-4 mr-1.5" />Confirm Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Email Sales Order {selected?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>To *</Label>
              <Input type="email" value={emailPayload.to_email} onChange={(e) => setEmailPayload({ ...emailPayload, to_email: e.target.value })} data-testid="cso-email-to" />
            </div>
            <div>
              <Label>Cc</Label>
              <Input
                type="email"
                multiple
                placeholder="comma-separated addresses"
                value={emailPayload.cc_email}
                onChange={(e) => setEmailPayload({ ...emailPayload, cc_email: e.target.value })}
                data-testid="cso-email-cc"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Auto-filled with the Sales Order creator's email — edit or clear as needed. Separate multiple addresses with commas.
              </div>
            </div>
            <div>
              <Label>Bcc</Label>
              <Input
                type="email"
                multiple
                placeholder="comma-separated addresses"
                value={emailPayload.bcc_email}
                onChange={(e) => setEmailPayload({ ...emailPayload, bcc_email: e.target.value })}
                data-testid="cso-email-bcc"
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={emailPayload.subject} onChange={(e) => setEmailPayload({ ...emailPayload, subject: e.target.value })} />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={3} value={emailPayload.message} onChange={(e) => setEmailPayload({ ...emailPayload, message: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="cso-email-send"><Mail className="w-4 h-4 mr-1.5" />Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raise DC dialog */}
      <Dialog open={raiseDcOpen} onOpenChange={setRaiseDcOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader><DialogTitle>Raise Delivery Challan for {selected?.code}</DialogTitle></DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 space-y-3" data-testid="cso-dc-body">
          {raiseDcLines ? (
            <div className="-mt-2 mb-1 space-y-2">
              <div className="text-xs text-slate-500">
                Raised directly against this Sales Order — no Packing Slip needed. Quantities default to what is still to be
                delivered; FG stock is checked when the DC is dispatched.
              </div>
              <table className="w-full text-sm" data-testid="cso-dc-lines">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    <th className="py-1">Item</th><th className="py-1">To Deliver</th><th className="py-1">DC Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {raiseDcLines.map((l, idx) => (
                    <tr key={l.item_id} className="border-t border-slate-100">
                      <td className="py-1 mono text-xs">{itemMap[l.item_id]?.code || l.item_id} <span className="text-slate-500">{itemMap[l.item_id]?.name}</span></td>
                      <td className="py-1 mono">{l.max}</td>
                      <td className="py-1">
                        <Input type="number" min={0} max={l.max} value={l.qty} className="w-24 h-8"
                          onChange={(e) => setRaiseDcLines((ls) => ls.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)))}
                          data-testid={`cso-dc-qty-${idx}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-slate-500 -mt-2 mb-1">
              Raised against this Sales Order and its packed Packing Slip {selected?.fulfilment?.open_packing_slip_code} — carries exactly the quantities packed from FG stock.
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label>Vehicle No</Label>
              <Input value={raiseDcForm.vehicle_no} onChange={(e) => setRaiseDcForm({ ...raiseDcForm, vehicle_no: e.target.value })} data-testid="cso-dc-vehicle" />
            </div>
            <div>
              <Label>Driver Name</Label>
              <Input value={raiseDcForm.driver_name} onChange={(e) => setRaiseDcForm({ ...raiseDcForm, driver_name: e.target.value })} />
            </div>
            <div>
              <Label>Transporter</Label>
              <Input value={raiseDcForm.transporter} onChange={(e) => setRaiseDcForm({ ...raiseDcForm, transporter: e.target.value })} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={raiseDcForm.notes} onChange={(e) => setRaiseDcForm({ ...raiseDcForm, notes: e.target.value })} />
            </div>
          </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRaiseDcOpen(false)}>Cancel</Button>
            <Button onClick={submitRaiseDc} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="cso-dc-submit">
              <Truck className="w-4 h-4 mr-1.5" />Raise DC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Master / detail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>Sales Order</span><span className="text-xs text-slate-500 mono">{visibleList.length}/{list.length}</span>
          </div>
          <div className="px-3 py-2 border-b border-slate-100 space-y-2">
            <Input
              placeholder="Search by SO/TO/DC code, customer, PO ref..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="cso-search-input"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs" data-testid="cso-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {visibleList.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-csos">No sales orders match.</div>}
            {visibleList.map((so) => (
              <div
                key={so.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelected(so)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(so); } }}
                data-testid={`cso-row-${so.code}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition cursor-pointer ${selected?.id === so.id ? "bg-blue-50/60" : ""} ${so.status === "cancelled" ? "opacity-60" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{so.code}</div>
                  <div className="flex items-center gap-1.5">
                    {so.email_sent && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-700" data-testid={`cso-sent-badge-${so.code}`}>Sent</span>
                    )}
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[so.status] || "bg-slate-100"}`}>{so.status}</span>
                    <OrderStockBadge info={orderStock[so.source_to_id]} testid={`cso-stock-badge-${so.code}`} />
                    <button
                      type="button"
                      title="Save & email this Sales Order to the customer"
                      onClick={(e) => { e.stopPropagation(); quickSendEmail(so); }}
                      disabled={busy}
                      className="p-1 rounded hover:bg-blue-100 text-slate-500 hover:text-blue-700 disabled:opacity-50"
                      data-testid={`cso-row-save-${so.code}`}
                    >
                      <Save className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-800">{custMap[so.customer_id]?.name || so.customer_name}</div>
                <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                  TO <span className="mono">{so.source_to_code}</span> · {formatMoney(Number(so.grand_total || 0), docCurrency(so))} · Del. {fmtDate(so.delivery_date)}
                  {so.status !== "cancelled" && pendingCount(so) > 0 && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                      {pendingCount(so)} pending
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="cso-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <ShoppingBag className="w-10 h-10 text-slate-300" /> Select a sales order to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.revision_number ? (selected.revision_no || revisionNo(selected.code, selected.revision_number)) : selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {custMap[selected.customer_id]?.name || selected.customer_name || "—"}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-1">
                    Transfer Order:{" "}
                    <button type="button" onClick={() => viewTransferOrder(selected)} className="mono text-blue-700 hover:underline inline-flex items-center gap-0.5" data-testid="cso-to-link">
                      {selected.source_to_code}<ExternalLink className="w-3 h-3" />
                    </button>
                    {selected.packing_slip_code && (
                      <>
                        {" · "}Packing Slip:{" "}
                        <button type="button" onClick={() => viewPackingSlip(selected)} className="mono text-blue-700 hover:underline inline-flex items-center gap-0.5" data-testid="cso-ps-link">
                          {selected.packing_slip_code}<ExternalLink className="w-3 h-3" />
                        </button>
                      </>
                    )}
                    {soDcs(selected).length > 0 && (
                      <>
                        {" · "}Delivery Challan{soDcs(selected).length > 1 ? "s" : ""}:{" "}
                        {soDcs(selected).map((d, i) => (
                          <span key={d.id || d.code} className="inline-flex items-center">
                            {i > 0 && <span className="mr-1">,</span>}
                            <button type="button" onClick={() => viewDc(selected, d.id)} className="mono text-blue-700 hover:underline inline-flex items-center gap-0.5" data-testid={i === 0 ? "cso-dc-link" : `cso-dc-link-${i}`}>
                              {d.code}<ExternalLink className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </>
                    )}
                    {" · "}Delivery Date: <span className="mono">{fmtDate(selected.delivery_date)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {selected.email_sent && (
                    <span
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-700"
                      title={selected.last_emailed_at ? `Last emailed ${new Date(selected.last_emailed_at).toLocaleString()}` : undefined}
                      data-testid="cso-sent-badge"
                    >
                      Sent
                    </span>
                  )}
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[selected.status] || "bg-slate-100"}`}>{selected.status}</span>
                </div>
              </div>

              {selected.status === "cancelled" && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-md" data-testid="cso-cancelled-banner">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Cancelled{selected.cancelled_by ? ` by ${selected.cancelled_by}` : ""}{selected.cancelled_at ? ` on ${new Date(selected.cancelled_at).toLocaleString()}` : ""}</div>
                    {selected.cancel_reason && <div>Reason: {selected.cancel_reason}</div>}
                  </div>
                </div>
              )}

              {selected.under_revision && (
                <div className="flex items-start gap-2 bg-purple-50 border border-purple-200 text-purple-800 text-xs px-3 py-2 rounded-md" data-testid="cso-under-revision-banner">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div>Transfer Order {selected.source_to_code} is being revised — this Sales Order is on hold and is refreshed when the TO is re-approved.</div>
                </div>
              )}

              {selected.status !== "cancelled" && pendingCount(selected) > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-md" data-testid="cso-pending-banner">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div>{pendingCount(selected)} line item(s) marked unavailable on the Proforma Invoice — still pending.</div>
                </div>
              )}

              {selected.status !== "cancelled" && selected.fulfilment && (selected.fulfilment.packed_qty > 0 || selected.fulfilment.dc_qty > 0) && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600 bg-slate-50 border border-slate-200 px-3 py-2 rounded-md" data-testid="cso-fulfilment">
                  <span>Ordered <span className="mono font-semibold">{selected.fulfilment.ordered_qty}</span></span>
                  <span>Packed <span className="mono font-semibold">{selected.fulfilment.packed_qty}</span></span>
                  <span>On DC <span className="mono font-semibold">{selected.fulfilment.dc_qty}</span></span>
                  <span className={selected.fulfilment.pending_pack_qty > 0 ? "text-amber-700" : ""}>
                    Pending to pack <span className="mono font-semibold">{selected.fulfilment.pending_pack_qty}</span>
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap" data-testid="cso-actions">
                {canEdit && !["dispatched", "cancelled"].includes(selected.status) && !selected.under_revision && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(selected)} data-testid="cso-edit-btn">
                    <FileEdit className="w-4 h-4 mr-1.5" />Edit
                  </Button>
                )}
                {canEdit && !["dispatched", "cancelled"].includes(selected.status) && !selected.under_revision && !selected.packing_locked && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(selected, "revise")} data-testid="cso-revise-btn">
                    <FileEdit className="w-4 h-4 mr-1.5" />Revise SO
                  </Button>
                )}
                <RevisionHistoryButton record={selected} baseNo={selected.code} testid="cso-revision-history" />
                {canBlockStock(user?.role) && selected.status !== "cancelled" && (
                  <StockBlockButton info={selectedStock.info} csoId={selected.id} onChanged={() => setStockTick((t) => t + 1)} testid="cso-action-stock" />
                )}
                {!["cancelled"].includes(selected.status) && canCreatePackingSlip && (selected.fulfilment
                  ? !selected.fulfilment.open_packing_slip_id && selected.fulfilment.pending_pack_qty > 0
                  : !selected.dc_id && !selected.packing_slip_id) && (
                  <Button variant="outline" size="sm" onClick={() => createPackingSlip(selected)} disabled={busy}
                    title="Pack this order from Finished Goods stock — only the qty available in FG is packed" data-testid="cso-create-ps-btn">
                    <PackageSearch className="w-4 h-4 mr-1.5" />
                    {selected.fulfilment?.packed_qty > 0 ? `Pack Remaining (${selected.fulfilment.pending_pack_qty})` : "Create Packing Slip"}
                  </Button>
                )}
                {selected.fulfilment?.open_packing_slip_id ? (
                  <Button variant="outline" size="sm" onClick={() => viewPackingSlip(selected, selected.fulfilment.open_packing_slip_id)} data-testid="cso-view-ps-btn">
                    <PackageSearch className="w-4 h-4 mr-1.5" />View Packing Slip {selected.fulfilment.open_packing_slip_code}
                  </Button>
                ) : (!selected.fulfilment && selected.packing_slip_id && !selected.dc_id && (
                  <Button variant="outline" size="sm" onClick={() => viewPackingSlip(selected)} data-testid="cso-view-ps-btn">
                    <PackageSearch className="w-4 h-4 mr-1.5" />View Packing Slip {selected.packing_slip_code}
                  </Button>
                ))}
                {/* Open slip still at zero (e.g. the pending balance after a DC) —
                    pack it from whatever FG stock is free now. */}
                {selected.fulfilment?.open_packing_slip_status === "draft" && canRepack && selected.status !== "cancelled" && (
                  <Button variant="outline" size="sm" onClick={() => packFromFg(selected.fulfilment.open_packing_slip_id)} disabled={busy} data-testid="cso-pack-fg-btn">
                    <PackageSearch className="w-4 h-4 mr-1.5" />Pack from FG Stock
                  </Button>
                )}
                {selected.fulfilment?.open_packing_slip_status === "packed" && canRepack && (
                  <Button variant="outline" size="sm" onClick={() => unpackSlip(selected.fulfilment.open_packing_slip_id)} disabled={busy}
                    className="text-orange-700 border-orange-200 hover:bg-orange-50" data-testid="cso-unpack-btn">
                    <PackageOpen className="w-4 h-4 mr-1.5" />Unpack
                  </Button>
                )}
                {/* Raise DC — from the packed Packing Slip when there is one,
                    otherwise directly against the Sales Order (no slip). */}
                {selected.status !== "cancelled" && canRaiseDc && !selected.under_revision && (
                  selected.fulfilment?.open_packing_slip_status === "packed" ? (
                    <Button size="sm" onClick={() => openRaiseDc(selected)} className="bg-blue-600 hover:bg-blue-700" data-testid="cso-raise-dc-btn">
                      <Truck className="w-4 h-4 mr-1.5" />Raise DC ({selected.fulfilment.open_packing_slip_packed_qty} packed)
                    </Button>
                  ) : selected.fulfilment?.pending_pack_qty > 0 && (
                    <Button size="sm" onClick={() => openRaiseDc(selected)} className="bg-blue-600 hover:bg-blue-700" data-testid="cso-raise-dc-btn">
                      <Truck className="w-4 h-4 mr-1.5" />Raise DC (no Packing Slip)
                    </Button>
                  )
                )}
                {soDcs(selected).map((d, i) => (
                  <Button key={d.id || d.code} variant="outline" size="sm" onClick={() => viewDc(selected, d.id)} data-testid={i === 0 ? "cso-view-dc-btn" : `cso-view-dc-btn-${i}`}>
                    <ClipboardCheck className="w-4 h-4 mr-1.5" />View DC {d.code}
                  </Button>
                ))}
                <Button variant="outline" size="sm" onClick={() => openTaxInvoices(selected)} data-testid="cso-tax-invoice-btn"><FileCheck2 className="w-4 h-4 mr-1.5" />Tax Invoice</Button>
                <Button variant="outline" size="sm" onClick={() => raisePi(selected)} data-testid="cso-raise-pi-btn"><Receipt className="w-4 h-4 mr-1.5" />Raise Proforma Invoice</Button>
                {/* Earlier PIs left items / qty short — raise one more for the balance. */}
                {selected.proforma_balance?.can_raise_balance && selected.status !== "cancelled" && (
                  <Button variant="outline" size="sm" onClick={() => raiseBalancePi(selected)} disabled={busy}
                    className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="cso-raise-balance-pi-btn">
                    <Receipt className="w-4 h-4 mr-1.5" />Raise Balance PI ({selected.proforma_balance.balance_qty})
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={printSO} data-testid="cso-print-btn"><Printer className="w-4 h-4 mr-1.5" />Print</Button>
                <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="cso-pdf-btn"><Download className="w-4 h-4 mr-1.5" />PDF</Button>
                <Button variant="outline" size="sm" onClick={() => openEmail()} data-testid="cso-email-btn"><Mail className="w-4 h-4 mr-1.5" />Send Email</Button>
                <Button
                  size="sm"
                  onClick={() => quickSendEmail(selected)}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  title="Send this Sales Order straight to the customer's email on file"
                  data-testid="cso-save-btn"
                >
                  <Save className="w-4 h-4 mr-1.5" />Save
                </Button>
                <OrderEmailStatus order={selected} resendPath={`/customer-sales-orders/${selected.id}/resend-email`}
                  label={`Sales Order ${selected.code}`} myEmail={user?.email} onDone={load} testid="cso-auto-email" />
                {selected.packing_locked && !["dispatched", "cancelled"].includes(selected.status) && (
                  <span className="text-[11px] text-slate-500" data-testid="cso-packing-locked">
                    Packing completed — revision locked ({selected.packing_lock_reason})
                  </span>
                )}
                {canCancel && !["dispatched", "cancelled"].includes(selected.status) && (
                  <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} className="ml-auto text-red-600 hover:text-red-700" data-testid="cso-cancel-btn">
                    <XCircle className="w-4 h-4 mr-1.5" />Cancel
                  </Button>
                )}
              </div>

              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic" data-testid="cso-notes">{selected.notes}</div>
              )}

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Line Items</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                        <th className="py-2 px-3">Part No</th>
                        <th className="py-2 px-3">Item</th>
                        <th className="py-2 px-3">Qty</th>
                        <th className="py-2 px-3">Rate</th>
                        <th className="py-2 px-3">Disc</th>
                        <th className="py-2 px-3">GST</th>
                        <th className="py-2 px-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.lines.map((l, idx) => {
                        const computed = selectedTotals?.lines_computed?.[idx];
                        return (
                          <tr key={idx} className="border-t border-slate-100" data-testid={`cso-line-${idx}`}>
                            <td className="py-2 px-3 mono text-xs text-slate-500">{itemMap[l.item_id]?.code || l.part_code || "—"}</td>
                            <td className="py-2 px-3">
                              <div className="text-slate-700 flex items-center gap-1.5">
                                {itemMap[l.item_id]?.name || l.part_name}
                                {l.pending && (
                                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">Pending</span>
                                )}
                              </div>
                              <PartialDeliveryList line={l} />
                            </td>
                            <td className="py-2 px-3 mono">{l.qty} {l.uom}</td>
                            <td className="py-2 px-3 mono">{formatMoney(Number(l.price), docCurrency(selected))}</td>
                            <td className="py-2 px-3 mono">{l.discount_pct || 0}%</td>
                            <td className="py-2 px-3 mono">{(l.cgst_pct || 0) + (l.sgst_pct || 0) + (l.igst_pct || 0)}%</td>
                            <td className="py-2 px-3 mono font-semibold">{formatMoney((computed?.line_total ?? l.qty * l.price), docCurrency(selected))}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <OrderStockPanel
                info={selectedStock.info}
                loading={selectedStock.loading}
                error={selectedStock.error}
                itemMap={itemMap}
                soId={selected.source_to_id}
                csoId={selected.id}
                canToggle={canBlockStock(user?.role) && selected.status !== "cancelled"}
                onChanged={() => setStockTick((t) => t + 1)}
                testid="cso-stock"
              />

              {selectedTotals && (
                <div className="flex justify-end">
                  <div className="bg-blue-50/50 border border-blue-100 rounded-md p-3 w-80 text-xs">
                    <div className="flex justify-between py-0.5"><span>Subtotal</span><span className="mono">{formatMoney(selectedTotals.subtotal, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>CGST</span><span className="mono">{formatMoney(selectedTotals.cgst_total, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>SGST</span><span className="mono">{formatMoney(selectedTotals.sgst_total, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>IGST</span><span className="mono">{formatMoney(selectedTotals.igst_total, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-0.5"><span>Discount</span><span className="mono">- {formatMoney(selectedTotals.discount_total, docCurrency(selected), { decimals: 2 })}</span></div>
                    <div className="flex justify-between py-1 mt-1 border-t border-blue-200 font-bold"><span>Grand Total</span><span className="mono" data-testid="cso-detail-grand">{formatMoney(selectedTotals.grand_total, docCurrency(selected), { withCode: true })}</span></div>
                  </div>
                </div>
              )}

              <div className="text-xs text-slate-500">
                Flow: <span className="mono">confirmed → dispatched</span>. Raising a DC attaches its number here; dispatching that DC marks this Sales Order Dispatched.
              </div>

              <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2" data-testid="cso-detail-audit">
                Created by <span className="mono">{selected.created_by || "—"}</span> on {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
                {selected.production_approved_by && <> · Production approved by <span className="mono">{selected.production_approved_by}</span></>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
